// mds-dojo — dijit/layout/_ContentPaneResizeMixin — FAITHFUL port of
// references/dijit/layout/_ContentPaneResizeMixin.js. AMD → ESM + classic declare
// from the faithful runtime. `../registry`/`../Viewport` map to dijit ids
// (Viewport → local ./_Viewport shim); domGeometry box helpers come from
// ./_geometry; layoutUtils from ./utils.
import array from '../../dojo/_base/array';
import declare from '../../dojo/_base/declare-runtime';
import domClass from '../../dojo/dom-class';
import domGeometry from '../../dojo/dom-geometry';
import domStyle from '../../dojo/dom-style';
import lang from '../../dojo/_base/lang';
import query from '../../dojo/query';
import registry from '../registry';
import Viewport from '../Viewport';
import layoutUtils from './utils';

export default declare('dijit.layout._ContentPaneResizeMixin', null, {
  // summary:
  //		Resize() functionality of ContentPane.

  // doLayout: Boolean
  doLayout: true,

  // isLayoutContainer: [protected] Boolean
  isLayoutContainer: true,

  startup: function () {
    // See `dijit/layout/_LayoutWidget.startup()` for description.
    if (this._started) {
      return;
    }

    var parent = this.getParent();
    this._childOfLayoutWidget = parent && parent.isLayoutContainer;

    // I need to call resize() on my child/children (when I become visible), unless
    // I'm the child of a layout widget in which case my parent will call resize() on me.
    this._needLayout = !this._childOfLayoutWidget;

    this.inherited(arguments);

    if (this._isShown()) {
      this._onShow();
    }

    if (!this._childOfLayoutWidget) {
      // Monitor viewport size changes so that I can re-layout.
      this.own(Viewport.on('resize', lang.hitch(this, 'resize')));
    }
  },

  _checkIfSingleChild: function () {
    // Test if we have exactly one visible widget as a child.
    if (!this.doLayout) { return; }

    var candidateWidgets: any[] = [],
      otherVisibleNodes = false;

    query('> *', this.containerNode).some(function (node: any) {
      var widget = registry.byNode(node);
      if (widget && widget.resize) {
        candidateWidgets.push(widget);
      } else if (!/script|link|style/i.test(node.nodeName) && node.offsetHeight) {
        otherVisibleNodes = true;
      }
    });

    this._singleChild = candidateWidgets.length == 1 && !otherVisibleNodes ?
      candidateWidgets[0] : null;

    // set overflow: hidden to avoid a safari bug w/scrollbars showing up (#9449)
    domClass.toggle(this.containerNode, this.baseClass + 'SingleChild', !!this._singleChild);
  },

  resize: function (changeSize: any, resultSize: any) {
    // See `dijit/layout/_LayoutWidget.resize()` for description.
    this._resizeCalled = true;

    this._scheduleLayout(changeSize, resultSize);
  },

  _scheduleLayout: function (changeSize: any, resultSize: any) {
    // Resize myself, and call resize() on my child layout widgets, either now
    // (if I'm currently visible) or when I become visible
    if (this._isShown()) {
      this._layout(changeSize, resultSize);
    } else {
      this._needLayout = true;
      this._changeSize = changeSize;
      this._resultSize = resultSize;
    }
  },

  _layout: function (changeSize: any, resultSize: any) {
    // Resize myself according to optional changeSize/resultSize parameters.
    delete this._needLayout;

    // For the TabContainer --> BorderContainer --> ContentPane case, _onShow() is
    // never called directly, so resize() is our trigger to do the initial href download.
    if (!this._wasShown && this.open !== false) {
      this._onShow();
    }

    // Set margin box size, unless it wasn't specified, in which case use current size.
    if (changeSize) {
      domGeometry.setMarginBox(this.domNode, changeSize);
    }

    // Compute content box size of containerNode in case we [later] need to size our single child.
    var cn = this.containerNode;
    if (cn === this.domNode) {
      var mb = resultSize || {};
      lang.mixin(mb, changeSize || {}); // changeSize overrides resultSize
      if (!('h' in mb) || !('w' in mb)) {
        mb = lang.mixin(domGeometry.getMarginBox(cn), mb); // fill in missing values
      }
      this._contentBox = layoutUtils.marginBox2contentBox(cn, mb);
    } else {
      this._contentBox = domGeometry.getContentBox(cn);
    }

    this._layoutChildren();
  },

  _layoutChildren: function () {
    // Call _checkIfSingleChild() again in case app has manually mucked w/the content.
    this._checkIfSingleChild();

    if (this._singleChild && this._singleChild.resize) {
      var cb = this._contentBox || domGeometry.getContentBox(this.containerNode);

      // note: if widget has padding this._contentBox will have l and t set,
      // but don't pass them to resize() or it will doubly-offset the child
      this._singleChild.resize({ w: cb.w, h: cb.h });
    } else {
      // All my child widgets are independently sized, but I still need to call
      // resize() on each child to make it layout.
      var children = this.getChildren(),
        widget,
        i = 0;
      while (widget = children[i++]) {
        if (widget.resize) {
          widget.resize();
        }
      }
    }
  },

  _isShown: function () {
    // Returns true if the content is currently shown.
    if (this._childOfLayoutWidget) {
      // If we are TitlePane, etc - we return that only *IF* we've been resized
      if (this._resizeCalled && 'open' in this) {
        return this.open;
      }
      return this._resizeCalled;
    } else if ('open' in this) {
      return this.open;		// for TitlePane, etc.
    } else {
      var node = this.domNode, parent = this.domNode.parentNode;
      return (node.style.display != 'none') && (node.style.visibility != 'hidden') && !domClass.contains(node, 'dijitHidden') &&
        parent && parent.style && (parent.style.display != 'none');
    }
  },

  _onShow: function () {
    // Called when the ContentPane is made visible
    this._wasShown = true;

    if (this._needLayout) {
      // If a layout has been scheduled for when we become visible, do it now
      this._layout(this._changeSize, this._resultSize);
    }

    this.inherited(arguments);
  }
});
